/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { OrderAddressDto2 } from '@/api-sdk';

/**
 * Maps the details of an address from an OrderAddressDto object into an array of formatted address lines.
 * Each line is created by concatenating the relevant address fields.
 *
 * @param address - The address object containing the details to be mapped.
 * @returns An array of formatted address lines.
 */
export function mapAddressDetails(address: OrderAddressDto2) {
  return [
    ...getAddressLine([address?.firstname, address?.lastname]),
    ...getAddressLine([address?.company]),
    ...getAddressLine([address?.streetName, address?.houseNumber]),
    ...getAddressLine([address?.city, address?.zipCode], ', '),
    ...getAddressLine([address?.country]),
    ...getAddressLine([address?.telephoneNumber]),
  ];
}

/**
 * Constructs a formatted address line from an array of address components.
 *
 * This function takes an array of address components and concatenates them into a single
 * formatted address line. If all components are empty or undefined, it returns an empty array.
 * The components are concatenated using a specified delimiter, which defaults to a space.
 *
 * @param addressItems - An array of address components to be concatenated.
 * @param concatValue - The delimiter used to concatenate the address components. Defaults to a space.
 * @returns An array containing a single formatted address line, or an empty array if all components are empty or undefined.
 */
function getAddressLine(
  addressItems: (string | undefined | null)[],
  concatValue = ' ',
): string[] {
  if (addressItems?.every(addressItem => !addressItem)) {
    return [];
  }

  const filteredAddressItems = addressItems?.filter(Boolean);
  const line = filteredAddressItems?.reduce(
    (previousValue: string, currentValue, currentIndex) => {
      if (currentValue) {
        return previousValue.concat(
          ...(currentIndex === 0
            ? [currentValue]
            : [concatValue, currentValue]),
        );
      }
      return previousValue;
    },
    '',
  );

  return [line];
}
